// Qui peut remplacer, pour UNE séance : disponibilité d'abord, puis expérience sur CET atelier / son pôle. Purement informatif : jamais un filtre
// (n'importe qui peut être désigné, une indisponibilité est signalée, jamais bloquante). Extrait de l'ancienne page (classementSeance) et complété :
// l'absence ne compte que si elle recoupe l'horaire de la séance (comme la détection Tipee), et « anime X à ce moment ».
import type { Absence, Atelier, HistoriqueRemplacement, Personne, SeanceProgramme } from '../../../types';
import { absenceRecoupeSeance, heureVersMinutes, parseTimeRange } from '../recoupement';
import { moisLong, type DateIso } from './dates';
import type { Seance } from './seancesDemande';

export type NiveauExp = 'fort' | 'moyen' | 'faible' | 'aucun';
const ORDRE: Record<NiveauExp, number> = { fort: 0, moyen: 1, faible: 2, aucun: 3 };

export interface Candidat {
  personne: Personne;
  niveau: NiveauExp;
  hist?: HistoriqueRemplacement;
  dispo: boolean;
  /** Pourquoi elle n'est pas disponible : « absent·e ce jour-là », « absent·e à ce moment », « anime « X » à ce moment ». */
  raison: string | null;
  /** Faux quand les absences n'ont pas pu être chargées : « disponible » ne veut alors rien dire (l'écran écrit « disponibilité non vérifiée »). */
  verifiee: boolean;
}

export interface ContexteClassement {
  personnes: Personne[];
  absentId: number | null;
  /** La gestion peut aussi désigner des externes ; l'intervenant·e, jamais. */
  avecExternes: boolean;
  seance: Pick<Seance, 'atelierId' | 'date' | 'debut' | 'fin'>;
  atelier: Pick<Atelier, 'pole_id'> | undefined;
  /** Historique de remplacement de CET atelier, par personne (vide tant qu'il n'est pas chargé). */
  hist: Record<number, HistoriqueRemplacement>;
  /** Absences (Tipee + manuelles) : seules celles de la date comptent. */
  absences: Absence[];
  /** Les absences ont bien été chargées (défaut : oui). Sinon, personne n'est dit « disponible » sans réserve. */
  absencesConnues?: boolean;
  /** Séances de TOUTE l'institution ce jour-là (pour « anime X à ce moment »). */
  seancesDuJour: SeanceProgramme[];
  battementMin: number;
}

/** Deux plages se recoupent (strictement) ; sans horaire connu d'un côté, on ne conclut pas à un conflit. */
function seChevauchent(a: { debut: string | null; fin: string | null }, b: { heure_debut: string | null; heure_fin: string | null }): boolean {
  const ad = heureVersMinutes(a.debut), af = heureVersMinutes(a.fin), bd = heureVersMinutes(b.heure_debut), bf = heureVersMinutes(b.heure_fin);
  if (ad == null || af == null || bd == null || bf == null) return false;
  return ad < bf && bd < af;
}

export function disponibilite(personneId: number, c: ContexteClassement): { dispo: boolean; raison: string | null; verifiee: boolean } {
  const verifiee = c.absencesConnues !== false;
  for (const a of c.absences) {
    if (a.personne_id !== personneId || String(a.date_absence).slice(0, 10) !== c.seance.date) continue;
    const plage = parseTimeRange(a.time_range, c.seance.date);
    if (absenceRecoupeSeance(plage, { heure_debut: c.seance.debut, heure_fin: c.seance.fin }, c.battementMin)) {
      return { dispo: false, raison: plage ? 'absent·e à ce moment' : 'absent·e ce jour-là', verifiee };
    }
  }
  for (const s of c.seancesDuJour) {
    if (s.atelier_id === c.seance.atelierId) continue;
    if ((s.animateur_personne_id === personneId || s.co_animateur_personne_id === personneId) && seChevauchent(c.seance, s)) {
      return { dispo: false, raison: `anime « ${s.atelier_nom} » à ce moment`, verifiee };
    }
  }
  return { dispo: true, raison: null, verifiee };
}

/** Candidat·e·s classé·e·s : disponibles d'abord, puis expérience (cet atelier, son pôle, même pôle), puis nombre de remplacements, puis nom. */
export function classer(c: ContexteClassement): Candidat[] {
  const pole = c.atelier?.pole_id ?? null;
  const liste = c.personnes
    .filter((p) => p.id !== c.absentId && p.actif !== false && (c.avecExternes || !p.is_externe))
    .map<Candidat>((p) => {
      const hist = c.hist[p.id];
      let niveau: NiveauExp = 'aucun';
      if (hist && hist.nb_atelier > 0) niveau = 'fort';
      else if (hist && hist.nb_pole > 0) niveau = 'moyen';
      else if (pole != null && p.pole_ids?.includes(pole)) niveau = 'faible';
      return { personne: p, hist, niveau, ...disponibilite(p.id, c) };
    });
  return liste.sort((a, b) =>
    (a.dispo === b.dispo ? 0 : a.dispo ? -1 : 1)
    || ORDRE[a.niveau] - ORDRE[b.niveau]
    || (b.hist?.nb_atelier ?? 0) - (a.hist?.nb_atelier ?? 0)
    || a.personne.display_name.localeCompare(b.personne.display_name, 'fr'));
}

/** « a remplacé cet atelier 3× (dernier : septembre 2026) » */
export function texteExperience(c: Candidat): string {
  if (c.niveau === 'fort' && c.hist) {
    const dernier = c.hist.dernier_atelier ? ` (dernier : ${moisLong(String(c.hist.dernier_atelier).slice(0, 10) as DateIso)})` : '';
    return `a remplacé cet atelier ${c.hist.nb_atelier}×${dernier}`;
  }
  if (c.niveau === 'moyen' && c.hist) return `a remplacé dans ce pôle ${c.hist.nb_pole}×`;
  if (c.niveau === 'faible') return 'même pôle';
  return 'pas encore remplacé ici';
}

/**
 * Qui peut recevoir un message personnel (« Prévenir aussi personnellement ») : interne, non stagiaire, active, avec un compte (sans compte, rien
 * ne partirait), jamais la personne absente. Les externes et les stagiaires n'ont pas l'onglet « Demandes des collègues » : le message ne leur
 * servirait à rien. Le serveur refuse (400) une personne externe ou la personne absente, ignore sans erreur un·e stagiaire ou une personne inactive,
 * et n'écrit pas à une personne sans compte (utils/prevenus.ts).
 */
export function peutEtrePrevenue(p: Personne, absentId: number | null): boolean {
  return p.id !== absentId && p.actif !== false && !p.is_externe && !p.is_stagiaire && p.a_un_compte !== false;
}

/** Les personnes à proposer en puces : disponibles et joignables, dans l'ordre du classement. */
export function aPrevenir(candidats: Candidat[], absentId: number | null): Candidat[] {
  return candidats.filter((c) => c.dispo && peutEtrePrevenue(c.personne, absentId));
}
